/** Geo item type of a pothole; the PH# number-clash rule applies to this type only. */
export const POTHOLE_GEO_ITEM_TYPE = 'potholeLog';

/** Prop label of a pothole's number. */
export const PH_NUMBER_LABEL = 'PH#';

/** The comparison key for a PH#: trimmed and lower-cased, or null when blank or not a string or number. */
export function phNumberKey(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const key = String(value).trim().toLowerCase();
  return key || null;
}

/** The PH# in a geo item's props, or undefined when it has none. */
export function phNumberOf(props: readonly { label: string; value: unknown }[] | null | undefined): unknown {
  return props?.find((p) => p.label === PH_NUMBER_LABEL)?.value;
}

/** Whether `candidateKey` is `<enteredKey>-<n>` for an integer `n >= 2` written without leading zeros. */
function isSuffixOf(candidateKey: string, enteredKey: string) {
  if (!candidateKey.startsWith(`${enteredKey}-`)) return false;
  const suffix = candidateKey.slice(enteredKey.length + 1);
  return /^[1-9]\d*$/.test(suffix) && Number(suffix) >= 2;
}

/**
 * The PH# to store for a pothole, given the keys ({@link phNumberKey}) of every other non-deleted
 * pothole's PH# in the project. An unused or blank number is stored as entered. A number in use
 * becomes `<entered>-<n>`: the pothole's own `current` value when it already has that form and is
 * still free, otherwise the lowest free `n >= 2`. Keeping `current` makes a resend of the plain
 * number land where the first save did, even after a lower suffix frees up.
 */
export function resolvePhNumber(entered: string, takenKeys: Iterable<string>, current?: unknown): string {
  const enteredKey = phNumberKey(entered);
  if (!enteredKey) return entered;
  const taken = new Set(takenKeys);
  if (!taken.has(enteredKey)) return entered;

  const currentKey = phNumberKey(current);
  if (currentKey && !taken.has(currentKey) && isSuffixOf(currentKey, enteredKey)) return String(current).trim();

  const base = entered.trim();
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/** One more than the highest leading integer among `values` (`7-2` counts as 7), or undefined when none has one. */
export function nextPhNumber(values: Iterable<unknown>): string | undefined {
  let highest: number | undefined;
  for (const value of values) {
    if (typeof value !== 'string' && typeof value !== 'number') continue;
    const match = /^\s*(\d+)/.exec(String(value));
    if (!match) continue;
    const n = Number(match[1]);
    if (Number.isSafeInteger(n) && (highest === undefined || n > highest)) highest = n;
  }
  return highest === undefined ? undefined : String(highest + 1);
}
